import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { EvaCompanyService } from "../../../features/eva-company/services/evaCompany.service";

// =============================================================================
// INTERFACES
// =============================================================================

/**
 * Types d'éléments pour les récents
 */
export type RecentItemType =
  | "project"
  | "invoice"
  | "quote"
  | "customer"
  | "employee"
  | "supplier"
  | "contact"
  | "intervention"
  | "purchase_order"
  | "contract"
  | "workshop"
  | "internal_article";

/**
 * Mapping des icônes par type d'élément
 */
export const RECENT_ITEM_ICONS: Record<RecentItemType, string> = {
  project: "heroBriefcase",
  invoice: "heroPencilSquare",
  quote: "heroDocument",
  customer: "heroUsers",
  employee: "heroUser",
  supplier: "heroTruck",
  contact: "heroIdentification",
  intervention: "heroWrench",
  purchase_order: "heroShoppingCart",
  contract: "heroDocumentCheck",
  workshop: "heroWrench",
  internal_article: "heroCube",
};

/**
 * Labels français des types
 */
export const RECENT_ITEM_LABELS: Record<RecentItemType, string> = {
  project: "Chantier",
  invoice: "Facture",
  quote: "Offre",
  customer: "Client",
  employee: "Collaborateur",
  supplier: "Fournisseur",
  contact: "Contact",
  intervention: "Intervention",
  purchase_order: "Bon de commande",
  contract: "Contrat",
  workshop: "Atelier",
  internal_article: "Article",
};

/**
 * Item récent dans l'historique
 */
export interface RecentItem {
  /** Identifiant unique de l'élément */
  id: string;
  /** Label affiché */
  label: string;
  /** Icône PrimeIcon */
  icon: string;
  /** Type d'élément */
  type: RecentItemType;
  /** Timestamp de l'accès */
  timestamp: number;
  /** Route de navigation (optionnel) */
  route?: string;
  /** Données supplémentaires */
  data?: any;
}

// =============================================================================
// SERVICE
// =============================================================================

/**
 * Service de gestion des éléments récents pour la Command Palette.
 *
 * Fonctionnalités :
 * - Stockage des 10 derniers éléments consultés
 * - Persistance en localStorage
 * - Icône automatique selon le type
 * - Déduplication des éléments
 *
 * @example
 * // Ajouter un élément récent
 * recentsService.addRecent({
 *   id: 'project-123',
 *   label: 'Chantier Villa Lausanne',
 *   type: 'project',
 *   route: '/projects/project-123'
 * });
 *
 * // Récupérer les récents
 * recentsService.recents$.subscribe(items => this.recents = items);
 */
@Injectable({
  providedIn: "root",
})
export class EvaCommandPaletteRecentsService {
  // ===========================================================================
  // Constants
  // ===========================================================================

  private readonly STORAGE_KEY_PREFIX = "eva-command-palette-recents";
  private readonly MAX_ITEMS = 10;

  // ===========================================================================
  // State
  // ===========================================================================

  private recentsSubject = new BehaviorSubject<RecentItem[]>([]);
  public recents$: Observable<RecentItem[]> = this.recentsSubject.asObservable();

  // ===========================================================================
  // Constructor
  // ===========================================================================

  constructor(private evaCompanyService: EvaCompanyService) {
    this.loadFromStorage();
  }

  private getStorageKey(): string {
    const companyId = this.evaCompanyService.currentEvaCompany?.eva_company_id || "default";
    return `${this.STORAGE_KEY_PREFIX}-${companyId}`;
  }

  // ===========================================================================
  // Public Methods
  // ===========================================================================

  /**
   * Ajoute un élément à l'historique des récents.
   * Si l'élément existe déjà, il est déplacé en première position.
   *
   * @param item Élément à ajouter (sans timestamp ni icône obligatoire)
   */
  addRecent(item: Omit<RecentItem, "timestamp" | "icon"> & { icon?: string }): void {
    const recents = this.recentsSubject.getValue();

    // Supprimer l'élément s'il existe déjà (déduplication)
    const filtered = recents.filter((r) => r.id !== item.id);

    // Créer le nouvel item avec timestamp et icône automatique
    const newItem: RecentItem = {
      ...item,
      icon: item.icon || RECENT_ITEM_ICONS[item.type] || "evaCircle",
      timestamp: Date.now(),
    };

    // Ajouter en première position et limiter à MAX_ITEMS
    const updated = [newItem, ...filtered].slice(0, this.MAX_ITEMS);

    this.recentsSubject.next(updated);
    this.saveToStorage(updated);
  }

  /**
   * Supprime un élément de l'historique.
   *
   * @param id Identifiant de l'élément à supprimer
   */
  removeRecent(id: string): void {
    const recents = this.recentsSubject.getValue();
    const updated = recents.filter((r) => r.id !== id);

    this.recentsSubject.next(updated);
    this.saveToStorage(updated);
  }

  /**
   * Vide tout l'historique des récents.
   */
  clearRecents(): void {
    this.recentsSubject.next([]);
    this.removeFromStorage();
  }

  /**
   * Retourne les récents actuels (snapshot).
   */
  getRecents(): RecentItem[] {
    return this.recentsSubject.getValue();
  }

  /**
   * Vérifie si l'historique contient des éléments.
   */
  hasRecents(): boolean {
    return this.recentsSubject.getValue().length > 0;
  }

  /**
   * Retourne l'icône par défaut pour un type donné.
   *
   * @param type Type d'élément
   */
  getIconForType(type: RecentItemType): string {
    return RECENT_ITEM_ICONS[type] || "evaCircle";
  }

  /**
   * Retourne le label français pour un type donné.
   *
   * @param type Type d'élément
   */
  getLabelForType(type: RecentItemType): string {
    return RECENT_ITEM_LABELS[type] || type;
  }

  // ===========================================================================
  // Private Methods - Storage
  // ===========================================================================

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      if (stored) {
        const parsed = JSON.parse(stored) as RecentItem[];
        // Valider et nettoyer les données
        const validated = parsed.filter((item) => item.id && item.label && item.type).slice(0, this.MAX_ITEMS);
        this.recentsSubject.next(validated);
      }
    } catch (error) {
      console.warn("[EvaCommandPaletteRecentsService] Erreur chargement localStorage:", error);
      this.recentsSubject.next([]);
    }
  }

  private saveToStorage(items: RecentItem[]): void {
    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify(items));
    } catch (error) {
      console.warn("[EvaCommandPaletteRecentsService] Erreur sauvegarde localStorage:", error);
    }
  }

  private removeFromStorage(): void {
    try {
      localStorage.removeItem(this.getStorageKey());
    } catch (error) {
      console.warn("[EvaCommandPaletteRecentsService] Erreur suppression localStorage:", error);
    }
  }
}
